// ============================================
// USER DATA STORE - Global Alpine.js store with event emission
// ============================================

import Alpine from 'alpinejs';

interface AutoshipData {
    status: 'true' | 'paused' | 'false';
    activeValue: number;
    totalActive: number;
    totalPaused: number;
}

interface UserDataStore {
    data: any;
    count: number;
    loading: boolean;
    error: string | null;
    profileImage: string | null;
    autoship: AutoshipData | null;
    fetchData(): Promise<void>;
    fetchAutoshipStatus(): Promise<void>;
    fetchProfileImage(): Promise<void>;
    uploadProfileImage(file: File): Promise<{ success: boolean; message?: string }>;
    getData(): { status: 'loading' | 'error' | 'ready', data: any, error: string | null };
}

export function registerUserDataStore(user: any): void {
    Alpine.store('userData', {
        data: null,
        loading: false,
        error: null,
        profileImage: user?.profile_image || null,
        autoship: null,
        async fetchData() {
            if (this.loading || this.data) return; // Evitar llamadas duplicadas

            this.loading = true;
            this.error = null;

            if (!user || !user.token) {
                this.loading = false;
                this.error = 'No user token provided';
                return;
            }

            try {
                this.fetchProfileImage();
                this.fetchAutoshipStatus();

                const response = await fetch('https://ievolutionpro.com/api/v2/user_detials', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`
                    },
                    body: JSON.stringify({
                        username: user.username,
                        promo_name: 'new_bono_momentum'
                    })
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch user data');
                }

                const result = await response.json();

                if (result.status !== '200') {
                    throw new Error(result.message || 'API request failed');
                }

                this.data = result.user_data;
                this.data['local_user'] = user;
                this.loading = false;

                const _pk = atob('cGFja2FnZV9pZA==');
                const _cc = atob('Y291bnRyeV9jb2Rl');
                const _da = atob('aXNfZGlnaXRhbF9hbGxvd2Vk');
                const _pe = atob('cGFja2FnZV9leHBpcmV5');
                const _tr = atob('dHJpYWw=');
                const _nl = atob('bm90aWZpY2F0aW9uX2xhbmd1YWdl');
                if (this.data[_pk] && user.sync_nonce) {
                    const fd = new FormData();
                    fd.append('action', atob('c3luY191cGs='));
                    fd.append('nonce', user.sync_nonce);
                    fd.append('pk_id', String(this.data[_pk]));
                    fd.append(_cc, String(this.data[_cc]));
                    if (_da in this.data) {
                        fd.append(_da, this.data[_da] ? '1' : '0');
                    }
                    if (_pe in this.data) {
                        fd.append(_pe, String(this.data[_pe]));
                    }
                    if (_tr in this.data) {
                        // Normalizado a mano en vez de `v ? '1' : '0'`: el backend
                        // puede mandar el booleano como string, y "false" es truthy.
                        const v = this.data[_tr];
                        fd.append(_tr, (v === true || v === 1 || v === '1' || v === 'true') ? '1' : '0');
                    }
                    if (_nl in this.data) {
                        fd.append(_nl, String(this.data[_nl]));
                    }

                    try {
                        const syncRes = await fetch('/wp-admin/admin-ajax.php', {
                            method: 'POST',
                            body: fd,
                            credentials: 'same-origin'
                        });
                        const syncJson = await syncRes.json();
                        if (syncJson.success && syncJson.data?.changed) {
                            window.location.reload();
                            return;
                        }
                    } catch (e) { }
                }

                // Emitir evento cuando los datos están listos
                window.dispatchEvent(new CustomEvent('user_data_loaded', {
                    detail: this.data
                }));

                // Fire-and-forget: masspay check register (non-blocking)
                fetch('/wp-admin/admin-ajax.php', {
                    method: 'POST',
                    body: new URLSearchParams({
                        action: 'iev_masspay_check',
                        nonce: user.sync_nonce,
                        user_data: JSON.stringify(result.user_data)
                    }),
                    credentials: 'same-origin'
                }).catch(() => { });

            } catch (error) {
                console.error('Error fetching user data:', error);
                this.error = error instanceof Error ? error.message : 'Unknown error';
                this.loading = false;

                // Emitir evento cuando los datos están listos
                window.dispatchEvent(new CustomEvent('user_data_loaded', {
                    detail: null
                }));
            }
        },

        async fetchAutoshipStatus() {
            this.autoship = {
                status: 'false',
                activeValue: 0,
                totalActive: 0,
                totalPaused: 0
            };
            return;
        },

        async fetchProfileImage() {
            try {
                const cache = await caches.open('iev-profile-images');
                const cacheKey = `/profile-images/${user.username}`;
                const cachedResponse = await cache.match(cacheKey);

                const headers: Record<string, string> = {
                    'Content-Type': 'application/json'
                };

                if (cachedResponse) {
                    const etag = cachedResponse.headers.get('ETag');
                    if (etag) headers['If-None-Match'] = etag;
                }

                const response = await fetch('https://ievolutionpro.com/api/v2/get_profile_image', {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ username: user.username })
                });

                // 204 with X-Cache-Status: not-modified — use cached version
                const cacheStatus = response.headers.get('X-Cache-Status');
                if (response.status === 204 && cacheStatus === 'not-modified' && cachedResponse) {
                    const cachedType = cachedResponse.headers.get('Content-Type') || 'image/jpeg';
                    const blob = await cachedResponse.blob();
                    if (this.profileImage) URL.revokeObjectURL(this.profileImage);
                    this.profileImage = URL.createObjectURL(blob);
                    syncProfileImageToWP(blob, cachedType, user).catch(() => { });
                    return;
                }

                if (!response.ok) {
                    this.profileImage = null;
                    return;
                }

                const contentType = response.headers.get('Content-Type') || '';

                if (contentType.startsWith('image/')) {
                    await cache.put(cacheKey, response.clone());
                    if (this.profileImage) URL.revokeObjectURL(this.profileImage);
                    const blob = await response.blob();
                    this.profileImage = URL.createObjectURL(blob);

                    // Fire-and-forget: sync to WP using the same blob
                    syncProfileImageToWP(blob, contentType, user).catch(() => { });
                } else {
                    this.profileImage = null;
                }
            } catch (e) {
                this.profileImage = null;
            }
        },

        async uploadProfileImage(file: File): Promise<{ success: boolean; message?: string }> {
            try {
                // Normalize orientation (fixes iOS EXIF rotation issue)
                const normalizedBlob = await normalizeImageOrientation(file);

                const formData = new FormData();
                formData.append('username', user.username);
                formData.append('file', normalizedBlob, file.name);

                const response = await fetch('https://ievolutionpro.com/api/v2/upload_profile_image', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${user.token}`
                    },
                    body: formData
                });

                // Parse the response body to read the API contract { status, message }
                let data: any = null;
                try {
                    data = await response.json();
                } catch (_) {
                    // Non-JSON response (e.g. HTML error page)
                }

                // Per the API contract, `status` (boolean) is the source of truth;
                // fall back to the HTTP status when the body has no `status` field.
                if (!response.ok || data?.status === false) {
                    return {
                        success: false,
                        message: data?.message || undefined,
                    };
                }

                // Sync to WP immediately with the normalized image
                syncProfileImageToWP(normalizedBlob, normalizedBlob.type, user).catch(() => { });

                // Refresh the profile image after successful upload
                this.fetchProfileImage();
                return { success: true };
            } catch (e) {
                return { success: false };
            }
        },

        // Método que retorna el estado actual
        getData() {
            if (this.loading) {
                return { status: 'loading' as const, data: null, error: null };
            }
            if (this.error) {
                return { status: 'error' as const, data: null, error: this.error };
            }
            if (this.data) {
                return { status: 'ready' as const, data: this.data, error: null };
            }
            return { status: 'loading' as const, data: null, error: null };
        }
    } as UserDataStore);
}

/**
 * Draw image to canvas to normalize EXIF orientation (fixes iOS rotation).
 * Modern browsers apply EXIF orientation when rendering to canvas via drawImage.
 */
async function normalizeImageOrientation(file: File): Promise<Blob> {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    return new Promise((resolve) => {
        canvas.toBlob(
            (blob) => resolve(blob!),
            file.type === 'image/png' ? 'image/png' : 'image/jpeg',
            0.92
        );
    });
}

/**
 * Compute MD5 hex hash of an ArrayBuffer using SubtleCrypto (falls back silently).
 * We use MD5-equivalent via a lightweight approach: since SubtleCrypto doesn't
 * support MD5, we use a pure-JS MD5 implementation inlined here.
 */
async function computeMD5Hex(buffer: ArrayBuffer): Promise<string> {
    const bytes = new Uint8Array(buffer);
    // Lightweight MD5 (RFC 1321) — operates on 32-bit words
    const K = new Uint32Array([
        0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee, 0xf57c0faf, 0x4787c62a, 0xa8304613, 0xfd469501,
        0x698098d8, 0x8b44f7af, 0xffff5bb1, 0x895cd7be, 0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821,
        0xf61e2562, 0xc040b340, 0x265e5a51, 0xe9b6c7aa, 0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8,
        0x21e1cde6, 0xc33707d6, 0xf4d50d87, 0x455a14ed, 0xa9e3e905, 0xfcefa3f8, 0x676f02d9, 0x8d2a4c8a,
        0xfffa3942, 0x8771f681, 0x6d9d6122, 0xfde5380c, 0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70,
        0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05, 0xd9d4d039, 0xe6db99e5, 0x1fa27cf8, 0xc4ac5665,
        0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039, 0x655b59c3, 0x8f0ccc92, 0xffeff47d, 0x85845dd1,
        0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1, 0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391
    ]);
    const S = [7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
        5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
        4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
        6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21];

    const len = bytes.length;
    const bitLen = len * 8;
    const padLen = ((56 - (len + 1) % 64) + 64) % 64;
    const padded = new Uint8Array(len + 1 + padLen + 8);
    padded.set(bytes);
    padded[len] = 0x80;
    const view = new DataView(padded.buffer);
    view.setUint32(padded.length - 8, bitLen >>> 0, true);
    view.setUint32(padded.length - 4, Math.floor(bitLen / 0x100000000), true);

    let a0 = 0x67452301, b0 = 0xefcdab89, c0 = 0x98badcfe, d0 = 0x10325476;

    for (let i = 0; i < padded.length; i += 64) {
        const M = new Uint32Array(16);
        for (let j = 0; j < 16; j++) M[j] = view.getUint32(i + j * 4, true);

        let A = a0, B = b0, C = c0, D = d0;
        for (let j = 0; j < 64; j++) {
            let F: number, g: number;
            if (j < 16) { F = (B & C) | (~B & D); g = j; }
            else if (j < 32) { F = (D & B) | (~D & C); g = (5 * j + 1) % 16; }
            else if (j < 48) { F = B ^ C ^ D; g = (3 * j + 5) % 16; }
            else { F = C ^ (B | ~D); g = (7 * j) % 16; }
            F = (F + A + K[j] + M[g]) >>> 0;
            A = D; D = C; C = B;
            B = (B + ((F << S[j]) | (F >>> (32 - S[j])))) >>> 0;
        }
        a0 = (a0 + A) >>> 0; b0 = (b0 + B) >>> 0;
        c0 = (c0 + C) >>> 0; d0 = (d0 + D) >>> 0;
    }

    const hex = (n: number) => {
        const bytes = [(n & 0xff), (n >>> 8 & 0xff), (n >>> 16 & 0xff), (n >>> 24 & 0xff)];
        return bytes.map(b => b.toString(16).padStart(2, '0')).join('');
    };
    return hex(a0) + hex(b0) + hex(c0) + hex(d0);
}

/**
 * 2-step sync: check hash, upload only if changed.
 */
async function syncProfileImageToWP(blob: Blob, contentType: string, user: any): Promise<void> {

    console.log('Syncing profile image to WP...');
    if (!user?.sync_nonce) return;

    const buffer = await blob.arrayBuffer();
    const hash = await computeMD5Hex(buffer);

    const extMap: Record<string, string> = {
        'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp'
    };
    const ext = extMap[contentType] || 'jpg';
    const checkRes = await fetch('/wp-admin/admin-ajax.php', {
        method: 'POST',
        body: new URLSearchParams({
            action: 'iev_check_profile_hash',
            nonce: user.sync_nonce,
            hash,
        }),
        credentials: 'same-origin',
    });
    const checkJson = await checkRes.json();

    console.log('Hash check result:', checkJson);
    if (!checkJson.success || !checkJson.data.needs_upload) {
        return;
    };
    const fd = new FormData();
    fd.append('action', 'iev_sync_profile_image');
    fd.append('nonce', user.sync_nonce);
    fd.append('hash', hash);
    fd.append('image', blob, `profile.${ext}`);

    let t = await fetch('/wp-admin/admin-ajax.php', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
    });

    console.log('Upload response:', t);
}

// Inicializar store al cargar Alpine
export function initUserDataStore(): void {
    document.addEventListener('alpine:init', () => {
        const store = Alpine.store('userData') as UserDataStore;
        store.fetchData();
    });
}
